// AbstractWizard.ts
import { WizardField, LLMMessage, LLMResponse, WizardConfig } from './type.js';
import { addDateContextToPrompt } from './wizardConfigs/wizardUtils';
// Change this

// To this
//import { WizardField, LLMMessage, LLMResponse, WizardConfig } from './wizardConfig.js';

export class AbstractWizard {
  protected fields: WizardField[];
  protected isComplete: boolean;
  protected userId: string;
  protected config: WizardConfig;
  public planningComplete: boolean = false;

  constructor(userId: string, config: WizardConfig) {
    this.userId = userId;
    this.config = config;
    this.fields = this.initializeFields();
    this.isComplete = false;
    this.planningComplete = false;
  }

  private initializeFields(): WizardField[] {
    return this.config.fieldDefinitions.map(def => ({
      name: def.name,
      required: def.required,
      value: def.defaultValue
    }));
  }

  // Add this public getter
  public getConfig(): WizardConfig {
    return this.config;
  }

  protected async callLLM_OLDOLDOLD(messages: LLMMessage[]): Promise<LLMResponse> {
    const apiKey = process.env.CLAUDE_API_KEY;

    // Add this log to see what config is being used
    console.log("Using wizard config:", this.config.id);
    console.log("Extraction prompt being used:", this.config.extractionPrompt);
    
    if (!apiKey) {
      console.error("Missing API_KEY in environment variables");
      return { extractedFields: {}, done: false };
    }
    
    console.log("Sending messages to Claude:", JSON.stringify(messages));
    
    try {
      /* const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
          'anthropic-version': '2023-06-01',
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          model: 'claude-3-haiku-20240307',
          system: this.config.extractionPrompt,
          messages: messages,
          max_tokens: 300,
          temperature: 0.0
        })
      }); */
      const res = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          system: prompt,
          messages: messages,
          temperature: 0.9,
          stream: true
        })
      });
      
      const fullResponse = await res.text();
      console.log("Full API response  :  Abstract Wizard --- ", fullResponse);
      
      let payload;
      try {
        payload = JSON.parse(fullResponse);
      } catch (e) {
        console.error("Failed to parse API response:", e);
        return { extractedFields: {}, done: false };
      }
      
      const rawText = payload.content?.[0]?.text ?? '';
      console.log("Raw LLM response : Abstract Wizard --- ", rawText);
      
      // Extract structured data 
      const extractedFields = this.extractStructuredData(rawText);
      
      // Update field values
      this.updateFieldValues(extractedFields);
      
      // Check completion
      const done = this.checkCompletion();
      
      return { 
        extractedFields,
        done,
        rawResponse: rawText // Add this line
      };
    } catch (error) {
      console.error("API call error:", error);
      return { extractedFields: {}, done: false };
    }
  }

  protected async callLLM(messages: LLMMessage[]): Promise<LLMResponse> {
    console.log("Using wizard config:", this.config.id);
    //console.log("Extraction prompt being used:", this.config.extractionPrompt);
    //const promptWithDateContext = addDateContextToPrompt(this.config.extractionPrompt);

    // NEW: Replace template variables in prompt
    let prompt = this.config.extractionPrompt;
    prompt = prompt.replace(/{{destination}}/g, this.getFieldValue('destination') || 'your destination');
    prompt = prompt.replace(/{{startDate}}/g, this.getFieldValue('startDate') || 'your dates');
    prompt = prompt.replace(/{{endDate}}/g, this.getFieldValue('endDate') || 'your dates');
   
    console.log('[AbstractWizard] Prompt after replacement:', prompt.substring(0, 200));
    
    try {
      const response = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          system: prompt,
          //system: this.config.extractionPrompt,
          //system:promptWithDateContext,
          messages: messages,
          temperature: 0.9,
          stream: true
        })
      });

      console.log("[Abstract Wizard] Prompt with formatting:", prompt.substring(0, 500));

      console.log(" [Abstract Wizard] PROCESS RECEIVED MESSAGES:", JSON.stringify(messages));
        
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
        
      const result = await response.json();
      const rawText = result.content?.[0]?.text ?? '';
      console.log("Raw LLM response : Abstract Wizard --- ", rawText);
        
      // Extract structured data 
      const extractedFields = this.extractStructuredData(rawText);
        
      // Update field values
      this.updateFieldValues(extractedFields);
        
      // Check completion
      const done = this.checkCompletion();
        
      return { 
        extractedFields,
        done,
        rawResponse: rawText
      };
    } catch (error) {
      console.error("API call error:", error);
      return { extractedFields: {}, done: false };
    } 
  }

  protected async callLLM_NEWANDNOTWORKING(messages: LLMMessage[]): Promise<LLMResponse> {
    console.log("Using wizard config:", this.config.id);
    
    // Prepare prompt with replacements
    let prompt = this.config.extractionPrompt;
    prompt = prompt.replace(/{{destination}}/g, this.getFieldValue('destination') || 'your destination');
    prompt = prompt.replace(/{{startDate}}/g, this.getFieldValue('startDate') || 'your dates');
    prompt = prompt.replace(/{{endDate}}/g, this.getFieldValue('endDate') || 'your dates');
    
    console.log('[AbstractWizard] Prompt after replacement:', prompt.substring(0, 200));
    
    try {
      const response = await fetch('/api/wizard', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          system: prompt,
          messages: messages,
          temperature: 0.9,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      // Initialize streaming variables
      let rawText = '';
      let jsonExtracted = false;
      let extractedData = {};
      
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      
      if (!reader) throw new Error('No reader available');
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');
          
          for (const line of lines) {
            if (line.trim() === '') continue;
            
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();
              
              // Skip end signal
              if (data === '[DONE]') continue;
              
              try {
                const parsed = JSON.parse(data);
                
                // Extract content from the chunk
                if (parsed.choices?.[0]?.delta?.content) {
                  const content = parsed.choices[0].delta.content;
                  rawText += content;
                  
                  // Try to extract JSON as soon as we have a complete object
                  if (!jsonExtracted && rawText.includes('{') && rawText.includes('}')) {
                    const jsonMatch = rawText.match(/\{[\s\S]*?\}/);
                    if (jsonMatch) {
                      try {
                        extractedData = JSON.parse(jsonMatch[0]);
                        jsonExtracted = true;
                        console.log('[Streaming] Extracted JSON:', extractedData);
                        
                        // Optional: Callback for early JSON extraction
                        this.onFieldsExtracted?.(extractedData);
                      } catch (e) {
                        // JSON not complete yet, continue
                      }
                    }
                  }
                  
                  // Optional: Update UI with streaming content
                  // Remove JSON from display text if needed
                  const displayText = jsonExtracted 
                    ? rawText.substring(rawText.indexOf('}') + 1).trim()
                    : rawText;
                  
                  this.onStreamingUpdate?.({
                    text: displayText,
                    extractedFields: extractedData,
                    isComplete: false
                  });
                }
                
                // Handle Anthropic format (if your API uses it)
                if (parsed.content?.[0]?.text) {
                  const content = parsed.content[0].text;
                  rawText += content;
                  // Similar JSON extraction logic here
                }
                
              } catch (parseError) {
                console.warn('Failed to parse streaming chunk:', parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
      
      console.log("Raw LLM response (complete):", rawText);
      
      // Final extraction if not done during streaming
      const extractedFields = jsonExtracted 
        ? extractedData 
        : this.extractStructuredData(rawText);
      
      // Update field values
      this.updateFieldValues(extractedFields);
      
      // Check completion
      const done = this.checkCompletion();
      
      // Final callback
      this.onStreamingUpdate?.({
        text: rawText,
        extractedFields,
        isComplete: true
      });
      
      return { 
        extractedFields,
        done,
        rawResponse: rawText
      };
      
    } catch (error) {
      console.error("API call error:", error);
      
      // Optional: Error callback
      this.onStreamingError?.(error);
      
      return { extractedFields: {}, done: false };
    }
  }

  // Optional: Add these callback methods to your class
  protected onStreamingUpdate?(update: {
    text: string;
    extractedFields: any;
    isComplete: boolean;
  }): void {
    // Override in subclass to handle streaming updates
    // Example: Update UI, show typing indicator, etc.
  }

  protected onFieldsExtracted?(fields: any): void {
    // Override in subclass to handle early field extraction
    // Example: Update form fields as soon as JSON is parsed
  }

  protected onStreamingError?(error: any): void {
    // Override in subclass to handle streaming errors
    // Example: Show error message to user
  }



  private extractStructuredData_UNUSED(text: string): Record<string, any> {
    console.log("Starting extractStructuredData with text:", text.substring(0, 100) + "...");

    // Try to find and parse a JSON object
    const jsonRegex = /\{[\s\S]*?\}/;
    const jsonMatch = text.match(jsonRegex);
    
    if (jsonMatch) {
      try {
        const jsonString = jsonMatch[0];
        const parsed = JSON.parse(jsonString);
        console.log("Extracted JSON:", parsed);
        return parsed;
      } catch (e) {
        console.error("Failed to parse JSON from response:", e);
      }
    }
    
    // Fallback: Use regex for each field
    console.log("Falling back to regex extraction");
    return this.extractWithRegex(text);
  }

  // In AbstractWizard.ts, add this method:
  public setFieldValue(fieldName: string, value: any): void {
    const field = this.fields.find(f => f.name === fieldName);
    if (field) {
      field.value = value;
    }
  }

  // In AbstractWizard.ts - extractStructuredData method
  private extractStructuredData(text: string): { extractedData: Record<string, any>, conversationalText: string } {
    console.log("[AbstractWizard] Full raw text:", text);

    const jsonRegex = /\{[\s\S]*?\}/;
    const jsonMatch = text.match(jsonRegex);
    let extractedData = {};
    let conversationalText = text;
    
    if (jsonMatch) {
      try {
        const jsonString = jsonMatch[0];
        const jsonStartIndex = text.indexOf(jsonString);
        const jsonEndIndex = jsonStartIndex + jsonString.length;
        
        extractedData = JSON.parse(jsonString);
        console.log("[AbstractWizard] Extracted JSON:", extractedData);
        
        // Get text after the JSON
        conversationalText = text.substring(jsonEndIndex).trim();
        console.log("[AbstractWizard] Conversational part:", conversationalText);
      } catch (e) {
        console.error("[AbstractWizard] JSON parsing error:", e);
      }
    }
    
    console.log("[AbstractWizard] Final extracted data:", extractedData);

    console.log("[AbstractWizard] Final conversational text:", conversationalText);

    return { extractedData, conversationalText };
  }

  /* private extractStructuredData(text: string): { extractedData: Record<string, any>, conversationalText: string } {
    console.log("[AbstractWizard] Full raw text:", text);

    const jsonRegex = /\{[\s\S]*?\}/;
    const jsonMatch = text.match(jsonRegex);
    let extractedData: Record<string, any> = {};
    let conversationalText = text;
    
    if (jsonMatch) {
      try {
        const jsonString = jsonMatch[0];
        const jsonStartIndex = text.indexOf(jsonString);
        const jsonEndIndex = jsonStartIndex + jsonString.length;
        
        extractedData = JSON.parse(jsonString);
        console.log("[AbstractWizard Offtopic] Extracted JSON:", extractedData);
        
        // Check for off-topic flag (TypeScript-safe)
        if ('offtopic' in extractedData && extractedData.offtopic === true) {
          console.log("[AbstractWizard Offtopic] Off-topic detected, breaking away");
          return { 
            extractedData: { breakAway: true }, 
            conversationalText: '' 
          };
        }
        
        // Get text after the JSON
        conversationalText = text.substring(jsonEndIndex).trim();
        console.log("[AbstractWizard] Conversational part:", conversationalText);
      } catch (e) {
        console.error("[AbstractWizard] JSON parsing error:", e);
      }
    }
    
    return { extractedData, conversationalText };
  } */


  private extractWithRegex(text: string): Record<string, any> {
    const result: Record<string, any> = {};
    
    // Generic field extraction function
    const extractField = (fieldName: string): any => {

      console.log(`Attempting to extract field: ${fieldName}`);

      // Try standard pattern first
      const standardRegex = new RegExp(`${fieldName}:?\\s*([^,\\n\\.]+)`, 'i');
      const match = text.match(standardRegex);
      
      if (match) {
        console.log(`Standard regex matched for ${fieldName}:`, match[1]);
        return match[1].trim();
      }
      
      // Special case for arrays
      const arrayRegex = new RegExp(`${fieldName}:?\\s*\\[(.*?)\\]`, 'i');
      const arrayMatch = text.match(arrayRegex);
      
      if (arrayMatch) {
        return arrayMatch[1].split(',')
          .map(s => s.trim().replace(/"/g, ''))
          .filter(s => s.length > 0);
      }
      
      return null;
    };
    
    // Extract each field defined in config
    for (const fieldDef of this.config.fieldDefinitions) {
      result[fieldDef.name] = extractField(fieldDef.name);
    }
    
    return result;
  }

  private updateFieldValues(extractedData: Record<string, any>): void {

    console.log(" [Abstract Wizard] EXTRACTED DATA:",extractedData);

    console.log(" [Abstract Wizard] FIELD VALUES BEFORE UPDATE:", JSON.stringify(this.fields.map(f => ({name: f.name, value: f.value}))));
    console.log(" [Abstract Wizard] NEW EXTRACTED DATA:", JSON.stringify(extractedData));

    for (const field of this.fields) {
      const newValue = extractedData[field.name];

      // && newValue !== null && newValue !== ''
      if (newValue !== undefined) {
        field.value = newValue;
      }
    }
  }

  // In AbstractWizard.ts
  private checkCompletion(): boolean {
    // First check if all fields except confirmation are filled
    const dataFieldsFilled = this.fields
      .filter(f => f.required && f.name !== 'confirmation')
      .every(f => {
        const placeholders = [null, undefined, '', 'Not provided', 'Not specified'];
        return !placeholders.includes(f.value);
      });

    // If data fields aren't filled, not complete
    if (!dataFieldsFilled) return false;

    // If all data fields are filled, check confirmation
    const confirmationField = this.fields.find(f => f.name === 'confirmation');
    return confirmationField?.value === true;
  }

  // In AbstractWizard.ts
  public isAwaitingConfirmation_OG(): boolean {
    const dataFieldsFilled = this.fields
      .filter(f => f.required && f.name !== 'confirmation')
      .every(f => f.value !== null && f.value !== undefined && f.value !== '');
    
    const confirmationField = this.fields.find(f => f.name === 'confirmation');
    return dataFieldsFilled && confirmationField?.value !== true;
  }

  public isAwaitingConfirmation(): boolean {
    const dataFieldsFilled = this.fields
      .filter(f => f.required && f.name !== 'confirmation')
      .every(f => {
        // Check for actual values, not just non-null
        if (Array.isArray(f.value)) {
          return f.value.length > 0;  // Empty arrays don't count
        }
        return f.value !== null && f.value !== undefined && f.value !== '';
      });
    
    const confirmationField = this.fields.find(f => f.name === 'confirmation');
    return dataFieldsFilled && confirmationField?.value !== true;
  }

  private checkCompletion_UNUSED2(): boolean {
    // Check if all required fields have valid values (not placeholders)
    return this.fields
      .filter(f => f.required)
      .every(f => {
        // Consider these values as incomplete
        const placeholders = [
          null, undefined, '', 
          'Not provided', 'Not specified', 
          'No specific destination mentioned'
        ];
        
        // Check if value is not a placeholder
        return !placeholders.includes(f.value);
      });
  }

  private checkCompletion_UNUSED1(): boolean {
    const isComplete = this.fields
      .filter(f => f.required)
      .every(f => f.value !== null && f.value !== undefined && f.value !== '');
    
    //console.log('checkCompletion result:', isComplete, 'fields:', this.fields);
    return isComplete;
  }

  public async process(messages: LLMMessage[]): Promise<LLMResponse> {
    const result = await this.callLLM(messages);
    const { extractedData, conversationalText } = this.extractStructuredData(result.rawResponse || '');
    
    // Update field values with the extracted data
    this.updateFieldValues(extractedData);

    
    // Check if we've collected all required fields
    this.isComplete = this.checkCompletion();

    console.log("check completion result:", this.isComplete)

    
    // Update planningComplete when needed
    if (this.isComplete && !this.planningComplete) {
      this.planningComplete = true;
      console.log('[AbstractWizard] PLANNING COMPLETE:', this.getFormattedOutput());
    }

    return {
      extractedFields: extractedData,
      done: this.isComplete,
      rawResponse: result.rawResponse,
      conversationalText: conversationalText // Add this to return the conversational part
    };
  }

  // In AbstractWizard.ts
/*   public async process_UNUSED(messages: LLMMessage[]): Promise<LLMResponse> {
    const result = await this.callLLM(messages);
    
    // Log the original values
    console.log(' [Abstract Wizard Old Logs] API result.done:', result.done);
    console.log(' [Abstract Wizard Old Logs] Manual check:', this.checkCompletion());
    
    // Use EITHER the API result OR our manual check
    this.isComplete = result.done || this.checkCompletion();
    
    console.log(' [Abstract Wizard Old Logs] Before updating fields:', this.fields);
    console.log(' [Abstract Wizard Old Logs] Updating with extracted fields:', result.extractedFields);
    console.log(' [Abstract Wizard Old Logs] After updating fields:', this.fields);
    console.log(' [Abstract Wizard Old Logs] FINAL isComplete value:', this.isComplete);

    // Update planningComplete when needed
    if (this.isComplete && !this.planningComplete) {
      this.planningComplete = true;
      console.log(' [Abstract Wizard Old Logs] >> PLANNING COMPLETE:', this.getFormattedOutput());
    }

    return {
      extractedFields: result.extractedFields,
      done: this.isComplete,
      rawResponse: result.rawResponse // Add this line to include raw response
    };
  }

  public async process_UNUSED2(messages: LLMMessage[]): Promise<LLMResponse> {
    const result = await this.callLLM(messages);
    const { extractedData, conversationalText } = this.extractStructuredData(result.rawResponse || '');
    
    // Update field values with the extracted data
    this.updateFieldValues(extractedData);
    
    // Check if we've collected all required fields
    this.isComplete = this.checkCompletion();
    
    // Update planningComplete when needed
    if (this.isComplete && !this.planningComplete) {
      this.planningComplete = true;
      console.log('[AbstractWizard] PLANNING COMPLETE:', this.getFormattedOutput());
    }

    return {
      extractedFields: extractedData,
      done: this.isComplete,
      rawResponse: result.rawResponse,
      conversationalText: conversationalText // Add this to return the conversational part
    };
  } */

  public getFieldValue(fieldName: string): any {
    return this.fields.find(f => f.name === fieldName)?.value ?? null;
  }

  public isWizardComplete(): boolean {
    return this.isComplete;
  }

  public getFormattedOutput(): any {
    if (this.config.formatFunc) {
      const fieldValues = Object.fromEntries(
        this.fields.map(f => [f.name, f.value])
      );
      console.log(" [Abstract Wizard] FIELD VALUES AFTER UPDATE:", JSON.stringify(this.fields.map(f => ({name: f.name, value: f.value}))));
      return this.config.formatFunc(fieldValues);
    }
    
    // Default formatting
    return {
      type: this.config.id,
      data: Object.fromEntries(this.fields.map(f => [f.name, f.value]))
    };
  }
}